import { max, min } from "mathjs";
import { DATA_CLEAN_FILTERS_NUM, DATA_TYPES } from "../../constants";
import { calcPercentile, convertToNumber } from "../../helpers/DataHelpers";
import { frequencyCounts } from "../../helpers/FrequencyCounts";
import Column from "../../MachineLearning/Column";
import Dataset from "../../MachineLearning/Dataset";
import { cleanDataFilterType } from "../../state";

function gt(a, b) {
  return convertToNumber(a) > convertToNumber(b);
}
function lt(a, b) {
  return convertToNumber(a) < convertToNumber(b);
}
function eq(a, b) {
  return a === b;
}
function neq(a, b) {
  return a !== b;
}

export function makeHistData(
  data: string[],
  colType: string,
  rowsToDrop: Object,
  meidan: number
) {
  if (colType === DATA_TYPES.number) {
    let histData = _makeHistData(
      Column.removeNaNs(Column.processNumberColumn(data), meidan)
    );
    return histData;
  } else {
    let rawFrequency = frequencyCounts(data);
    let keys = Object.keys(rawFrequency);
    let vals = Object.values(rawFrequency);
    let histData: any[] = [["", "Items"]];
    for (let i = 0; i < keys.length; i++) {
      histData.push([keys[i], vals[i]]);
    }
    return histData;
  }
}

export function getFilterFn(filter: cleanDataFilterType) {
  let fn = undefined;
  if (filter.operation === DATA_CLEAN_FILTERS_NUM.equal) {
    fn = eq;
  } else if (filter.operation === DATA_CLEAN_FILTERS_NUM.greater) {
    fn = gt;
  } else if (filter.operation === DATA_CLEAN_FILTERS_NUM.less) {
    fn = lt;
  } else if (filter.operation === DATA_CLEAN_FILTERS_NUM.not_equal) {
    fn = neq;
  }
  return fn;
}

export function getColData(dataset: Dataset, colName: string) {
  let col = dataset.getColumn(colName);
  if (col.columnType === DATA_TYPES.number) {
    return col.vectorizedData;
  } else if (col.availableColumnTypes.includes(DATA_TYPES.number)) {
    return dataset.data[colName].map((val) => col.getClassIndex(val));
  } else {
    return dataset.data[colName];
  }
}

export function convertVal(filter: cleanDataFilterType, dataset: Dataset) {
  if (
    dataset
      .getColumn(filter.column)
      .availableColumnTypes.includes(DATA_TYPES.number)
  ) {
    let value = convertToNumber(filter.value);
    let replace_with = filter.drop_row
      ? filter.replace_with
      : convertToNumber(filter.replace_with);
    return { value: value, replace_with: replace_with };
  } else {
    return { value: filter.value, replace_with: filter.replace_with };
  }
}

function filterColumn(
  dataset: Dataset,
  colName: string,
  filters: cleanDataFilterType[]
) {
  let colData = getColData(dataset, colName);
  let dropFilters = filters.filter((f) => f.drop_row);
  let filtered: any[] = [];
  let toDrop: Set<number> = new Set();

  for (let filter of dropFilters) {
    let fn = getFilterFn(filter);
    let data = getColData(dataset, filter.column);
    let { value, replace_with } = convertVal(filter, dataset);
    for (let i = 0; i < data.length; i++) {
      if (fn(data[i], value)) {
        toDrop.add(i);
      }
    }
  }
  for (let i = 0; i < colData.length; i++) {
    if (!toDrop.has(i)) {
      filtered.push(colData[i]);
    }
  }

  let nonDropFilters = filters.filter(
    (f) => !f.drop_row && f.column == colName
  );
  let rowsModified: Set<string> = new Set();

  for (let filter of nonDropFilters) {
    let fn = getFilterFn(filter);
    let { value, replace_with } = convertVal(filter, dataset);
    for (let i = 0; i < filtered.length; i++) {
      if (fn(filtered[i], value)) {
        filtered[i] = replace_with;
        rowsModified.add(i + filter.column);
      }
    }
  }
  if (dataset.getColumn(colName).columnType !== DATA_TYPES.number) {
    filtered = filtered.map((v) => v + "");
  }
  return filtered;
}

export function _makeHistData(data: number[]) {
  if (data.length === 0) {
    return [] as number[][];
  }
  let IQR =
    calcPercentile(data, 0.75, DATA_TYPES.number) -
    calcPercentile(data, 0.25, DATA_TYPES.number);
  // if (Math.abs(IQR) < 0.0001) {
  // 	return [[data[0], data.length]];
  // }

  if (Math.abs(IQR) < 0.0001) {
    IQR = max(data) - min(data);
  }
  if (Math.abs(IQR) < 0.0001) {
    IQR = 1;
  }

  let lowerBound = min(data);
  const upperBound = max(data);

  // Formulaic Bins
  let binWidth = (2 * IQR) / Math.pow(data.length, 1 / 3);
  if (binWidth < 0.00001) {
    return [] as number[][];
  }
  let nBins = Math.max(Math.ceil((upperBound - lowerBound) / binWidth), 1);
  if (nBins > 200) {
    nBins = 200;
    binWidth = (upperBound - lowerBound) / nBins;
  }

  // Try and set integer bin widths
  let binWdithInt = Math.max(Math.floor(binWidth), 1);
  let lowerBountInt = Math.floor(lowerBound);
  let nBinsInt = Math.ceil(
    (upperBound - lowerBountInt) / Math.max(binWdithInt, 0.001)
  );
  if (nBinsInt >= 4) {
    lowerBound = lowerBountInt;
    binWidth = binWdithInt;
    nBins = nBinsInt;
  }

  let lowerBounds: number[] = Array(nBins)
    .fill(0)
    .map((v, i) => lowerBound + i * binWidth);

  const counts = Array(nBins).fill(0);
  for (let val of data) {
    for (let i = 0; i < lowerBounds.length; i++) {
      if (i === lowerBounds.length - 1) {
        counts[i] += 1;
      } else if (val >= lowerBounds[i] && val < lowerBounds[i + 1]) {
        counts[i] += 1;
        break;
      }
    }
  }

  let outData: number[][] = [];
  for (let i = 0; i < counts.length; i++) {
    outData.push([lowerBounds[i], counts[i]]);
  }

  //Filter zeros from bottom
  let lower = 0;
  for (let i = 0; i < outData.length; i++) {
    if (outData[i][1] !== 0) {
      break;
    }
    lower = i;
  }
  let upper = outData.length - 1;
  for (let i = outData.length - 1; i >= 0; i--) {
    if (outData[i][1] !== 0) {
      break;
    }
    upper = i;
  }

  outData = outData.slice(lower, upper + 1);

  return outData;
}
